/**
 * Verificação de prontidão offline (PLAN §20).
 *
 * É a funcionalidade mais importante do projeto (PLAN §60):
 *
 * > Antes de viajar, o usuário consegue verificar objetivamente que o glossário e os
 * > vídeos selecionados estão realmente disponíveis sem internet.
 *
 * "Objetivamente" é a palavra que define o desenho: **nada aqui confia numa flag do
 * banco**. Cada arquivo é procurado no filesystem, o tamanho é lido do próprio
 * arquivo, e o checksum é conferido quando existe. Um relatório que apenas repetisse
 * o que o IndexedDB afirma seria inútil justamente no caso que importa — o iOS ter
 * evictado os arquivos sem avisar (F14-T11, PLAN F10-T11).
 */
import { db } from '@/db/schema';
import { getCatalogMetadata } from '@/db/repositories/catalog';
import { updateState } from '@/db/repositories/localSignState';
import { sha256Hex } from '@/services/media/validation';
import { getFileStore } from '@/storage/index';
import type { OfflineFileStore } from '@/storage/types';

export type CheckStatus = 'ok' | 'warning' | 'error';

export interface ReadinessCheck {
  id: 'shell' | 'catalog' | 'files';
  label: string;
  status: CheckStatus;
  detail: string;
}

export interface ReadinessReport {
  checkedAt: string;
  ready: boolean;
  checks: ReadinessCheck[];

  videos: {
    expected: number;
    present: number;
    /** Marcados como disponíveis no banco, mas ausentes do filesystem. */
    missing: string[];
    /** Presentes no filesystem com tamanho diferente do registrado. */
    sizeMismatch: string[];
    /** Presentes, mas com checksum diferente do informado pela origem. */
    checksumMismatch: string[];
    /** Arquivos sem sinal correspondente no catálogo. */
    orphans: string[];
    /** Arquivo cujo vídeo mudou na fonte. */
    outdated: string[];
  };

  catalog: {
    present: boolean;
    version?: string;
    signs: number;
    categories: number;
  };
}

export interface VerifyOptions {
  /** Confere o SHA-256 de cada arquivo. Custa ler tudo; desligado por padrão. */
  verifyChecksums?: boolean;
  store?: OfflineFileStore;
  onProgress?: (done: number, total: number) => void;
}

/** O shell só está garantido se o Service Worker tiver o precache preenchido. */
async function checkShell(): Promise<ReadinessCheck> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return {
      id: 'shell',
      label: 'Aplicação',
      status: 'error',
      detail: 'Este navegador não suporta Service Worker; o app não abrirá sem internet.',
    };
  }

  const registration = await navigator.serviceWorker.getRegistration().catch(() => null);
  if (!registration?.active) {
    return {
      id: 'shell',
      label: 'Aplicação',
      status: 'error',
      detail: 'O app ainda não foi armazenado no dispositivo. Recarregue com internet.',
    };
  }

  // A presença de um cache do Workbox com entradas é a prova de que o shell está
  // gravado — a existência do registro sozinha não garante isso.
  const cacheNames = await caches.keys().catch(() => [] as string[]);
  const precacheName = cacheNames.find((name) => name.includes('precache'));

  if (!precacheName) {
    return {
      id: 'shell',
      label: 'Aplicação',
      status: 'error',
      detail: 'O conteúdo do app não está em cache. Recarregue com internet.',
    };
  }

  const entries = await caches.open(precacheName).then((cache) => cache.keys());

  return entries.length > 0
    ? {
        id: 'shell',
        label: 'Aplicação',
        status: 'ok',
        detail: `${entries.length} arquivos do app armazenados.`,
      }
    : {
        id: 'shell',
        label: 'Aplicação',
        status: 'error',
        detail: 'O cache do app está vazio. Recarregue com internet.',
      };
}

export async function verifyOfflineReadiness(
  options: VerifyOptions = {},
): Promise<ReadinessReport> {
  const { verifyChecksums = false, store = getFileStore(), onProgress } = options;
  const checkedAt = new Date().toISOString();

  const shell = await checkShell();

  // --- catálogo local ---
  const metadata = await getCatalogMetadata();
  const [signCount, categoryCount] = await Promise.all([
    db.signs.count(),
    db.categories.count(),
  ]);

  const catalogCheck: ReadinessCheck = metadata
    ? {
        id: 'catalog',
        label: 'Catálogo',
        status: signCount > 0 ? 'ok' : 'error',
        detail:
          signCount > 0
            ? `${signCount} sinais em ${categoryCount} categorias (versão ${metadata.catalogVersion}).`
            : 'O catálogo está vazio.',
      }
    : {
        id: 'catalog',
        label: 'Catálogo',
        status: 'error',
        detail: 'Nenhum catálogo carregado. Conecte-se à internet e sincronize.',
      };

  // --- arquivos ---
  const states = await db.localSignState.toArray();
  const expectedStates = states.filter((state) => state.offline.status === 'available');
  const filesOnDisk = new Set(await store.list().catch(() => [] as string[]));
  const knownSignIds = new Set(await db.signs.toCollection().primaryKeys());

  const missing: string[] = [];
  const sizeMismatch: string[] = [];
  const checksumMismatch: string[] = [];
  const outdated: string[] = [];
  let present = 0;

  for (const [index, state] of expectedStates.entries()) {
    onProgress?.(index, expectedStates.length);

    const { signId } = state;

    // A verdade é o filesystem, não a flag.
    if (!filesOnDisk.has(signId)) {
      missing.push(signId);
      continue;
    }

    const actualSize = await store.getSize(signId).catch(() => null);
    if (actualSize === null || actualSize === 0) {
      missing.push(signId);
      continue;
    }

    if (state.offline.totalBytes !== undefined && actualSize !== state.offline.totalBytes) {
      sizeMismatch.push(signId);
      continue;
    }

    if (verifyChecksums && state.offline.checksum) {
      const blob = await store.read(signId).catch(() => null);
      const actual = blob ? await sha256Hex(blob) : null;
      if (actual?.toLowerCase() !== state.offline.checksum.toLowerCase()) {
        checksumMismatch.push(signId);
        continue;
      }
    }

    if (state.offline.outdated) outdated.push(signId);
    present += 1;
  }

  onProgress?.(expectedStates.length, expectedStates.length);

  const orphans = [...filesOnDisk].filter((signId) => !knownSignIds.has(signId));

  const problems = missing.length + sizeMismatch.length + checksumMismatch.length;
  const filesCheck: ReadinessCheck = {
    id: 'files',
    label: 'Arquivos',
    status: problems > 0 ? 'error' : outdated.length > 0 ? 'warning' : 'ok',
    detail:
      problems > 0
        ? `${problems} arquivo(s) com problema de ${expectedStates.length} esperados.`
        : outdated.length > 0
          ? `${present} arquivos íntegros, ${outdated.length} desatualizados.`
          : expectedStates.length === 0
            ? 'Nenhum vídeo baixado.'
            : `${present} de ${expectedStates.length} arquivos íntegros.`,
  };

  const checks = [shell, catalogCheck, filesCheck];

  return {
    checkedAt,
    // Pronto para viajar = app em cache, catálogo presente e nenhum arquivo quebrado.
    // Ter zero vídeos é um estado válido: o glossário em si já funciona offline.
    ready: checks.every((check) => check.status !== 'error'),
    checks,
    videos: {
      expected: expectedStates.length,
      present,
      missing,
      sizeMismatch,
      checksumMismatch,
      orphans,
      outdated,
    },
    catalog: {
      present: metadata !== undefined,
      ...(metadata ? { version: metadata.catalogVersion } : {}),
      signs: signCount,
      categories: categoryCount,
    },
  };
}

export interface RepairResult {
  reset: number;
  orphansRemoved: number;
}

/**
 * Corrige as inconsistências encontradas (F14-T10).
 *
 * Arquivos ausentes ou corrompidos voltam ao estado `none`, para que possam ser
 * baixados de novo. Órfãos são removidos apenas quando pedido explicitamente — um
 * sinal que saiu do catálogo pode voltar (PLAN §41).
 */
export async function repairReadiness(
  report: ReadinessReport,
  options: { removeOrphans?: boolean; store?: OfflineFileStore } = {},
): Promise<RepairResult> {
  const { removeOrphans = false, store = getFileStore() } = options;

  const broken = [
    ...report.videos.missing,
    ...report.videos.sizeMismatch,
    ...report.videos.checksumMismatch,
  ];

  for (const signId of broken) {
    await store.remove(signId).catch(() => undefined);
    await updateState(signId, (current) => ({
      ...current,
      offline: {
        status: 'none',
        error: 'O arquivo local não passou na verificação e precisa ser baixado de novo.',
      },
    }));
  }

  let orphansRemoved = 0;
  if (removeOrphans) {
    for (const signId of report.videos.orphans) {
      await store.remove(signId).catch(() => undefined);
      orphansRemoved += 1;
    }
  }

  return { reset: broken.length, orphansRemoved };
}
